import React from 'react';
import {
  Box,
  Grid,
  Button,
  Icon,
  HStack,
  Heading,
  Text,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import type { Key } from 'rc-tree/lib/interface';
import { MonitoringDataNode } from 'queries/monitoring';
import { Card } from 'layouts';
import {
  getAllTreeKeys,
  useDeleteMonitoringCodes,
  getAllSelectedCodeIds,
  filterTreeNodesByCodeIds,
} from 'queries/monitoring';
import Tree from 'rc-tree';
import { FiChevronRight, FiArrowDown, FiArrowUp } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { campaignCodesAssignPage } from '../Campaign.Links';

// 'manage' = zarządzanie całą pulą kodów kampanii (modal uploadu, niezależnie od transakcji);
// 'assign' = przypisywanie/odpinanie kodów do jednej, wybranej transakcji (Panel dopasowywania)
type MonitoringCodesTreeMode = 'manage' | 'assign';

const ExpandCollapseControls = ({
  mode,
  onCollapse,
  onExpand,
  isDisabled,
}: {
  mode: MonitoringCodesTreeMode;
  onCollapse: () => void;
  onExpand: () => void;
  isDisabled: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const collapseLabel = t('monitoring.collapseAll', 'Zwiń wszystko');
  const expandLabel = t('monitoring.expandAll', 'Rozwiń wszystko');

  /*   if (mode === 'assign') {
    return (
      <HStack
        gap={1}
        justifyContent='flex-end'
        position={'absolute'}
        top={6}
        right={4}
      >
        <IconButton
          aria-label={collapseLabel}
          title={collapseLabel}
          icon={<FiArrowUp />}
          onClick={onCollapse}
          isDisabled={isDisabled}
          size='sm'
          variant='ghost'
        />
        <IconButton
          aria-label={expandLabel}
          title={expandLabel}
          icon={<FiArrowDown />}
          onClick={onExpand}
          isDisabled={isDisabled}
          size='sm'
          variant='ghost'
        />
      </HStack>
    );
  }
 */

  return (
    <HStack
      gap={2}
      {...(mode === 'assign' && {
        justifyContent: 'flex-end',
        position: 'absolute',
        top: '4',
        right: '4',
      })}
    >
      <Button
        onClick={onCollapse}
        leftIcon={<FiArrowUp />}
        isDisabled={isDisabled}
        size='xs'
      >
        {collapseLabel}
      </Button>
      <Button
        onClick={onExpand}
        leftIcon={<FiArrowDown />}
        isDisabled={isDisabled}
        size='xs'
      >
        {expandLabel}
      </Button>
    </HStack>
  );
};

export const MonitoringCodesTree = ({
  codesNodes,
  codesType,
  campaignId,
  selectedProposalId,
  assignedProposalCodeIds,
  onCheckedCodeIdsChange,
  mode = 'manage',
  hideSelectProposalHint = false,
  ...propsTree
}: {
  codesNodes: MonitoringDataNode[];
  codesType: 'available' | 'assigned';
  campaignId: number;
  selectedProposalId?: number | null;
  assignedProposalCodeIds?: number[];
  onCheckedCodeIdsChange?: (ids: number[]) => void;
  mode?: MonitoringCodesTreeMode;
  hideSelectProposalHint?: boolean;
  [key: string]: any;
}): JSX.Element => {
  const { t } = useTranslation();
  const deleteCodes = useDeleteMonitoringCodes();

  /**
   * assigned - show and endit ONLY codes assigned to this proposal (deleting);
   * available - the pool of unassigned codes is not filtered, assignedProposalCodeIds is only used for merging when saving (adding)
   */
  const isEditingAssignedCodesOfProposal =
    codesType === 'assigned' && assignedProposalCodeIds !== undefined;

  const displayedCodes = React.useMemo(
    () =>
      isEditingAssignedCodesOfProposal
        ? filterTreeNodesByCodeIds(codesNodes, assignedProposalCodeIds ?? [])
        : codesNodes,
    [codesNodes, isEditingAssignedCodesOfProposal, assignedProposalCodeIds],
  );

  console.log('displayedCodes', displayedCodes);

  const [expandedKeys, setExpandedKeys] = React.useState<Key[]>(() =>
    codesType === 'assigned' ? getAllTreeKeys(displayedCodes) : [],
  );
  const [selectedCodeIds, setSelectedCodeIds] = React.useState<number[]>(() =>
    isEditingAssignedCodesOfProposal ? assignedProposalCodeIds ?? [] : [],
  );

  React.useEffect(() => {
    onCheckedCodeIdsChange?.(selectedCodeIds);
  }, []);

  const isDisabledWithoutProposalSelected =
    mode === 'assign' && !selectedProposalId;

  const expandAll = () => {
    setExpandedKeys(getAllTreeKeys(displayedCodes ?? []));
  };
  const collapseAll = () => {
    setExpandedKeys([]);
  };

  const handleCheck = (
    _checked: Key[] | { checked: Key[]; halfChecked: Key[] },
    info: { checkedNodes: MonitoringDataNode[] },
  ) => {
    const ids = getAllSelectedCodeIds(info.checkedNodes);
    setSelectedCodeIds(ids);
    onCheckedCodeIdsChange?.(ids);
  };

  const handleDeleteCodes = () => {
    deleteCodes.mutate(
      { campaignId, codes: selectedCodeIds },
      { onSuccess: () => setSelectedCodeIds([]) },
    );
  };

  const title =
    codesType === 'assigned'
      ? t('monitoring.codesAssigned', 'Przypisane kody')
      : t('monitoring.codesAvailable', 'Nieprzypisane kody');

  const treeContent = (
    <>
      {isDisabledWithoutProposalSelected && !hideSelectProposalHint && (
        <Text fontSize='sm' color='palette.gray.main'>
          {t(
            'monitoring.selectProposalHint',
            'Wybierz transakcję z listy po lewej, aby przypisać do niej kody',
          )}
        </Text>
      )}
      <Box minW={0} overflow='hidden'>
        {displayedCodes.length === 0 ? (
          <Text fontWeight='bold' fontSize='xs'>
            {isEditingAssignedCodesOfProposal
              ? t(
                  'monitoring.assignedCodesEmptyForProposal',
                  'Do tej transakcji nie przypisano jeszcze żadnych kodów',
                )
              : codesType === 'assigned'
              ? t(
                  'monitoring.assignedCodesEmpty',
                  'Żaden z kodów nie został przypisany do transakcji',
                )
              : t(
                  'monitoring.availableCodesEmpty',
                  'Wszystkie kody zostały przypisane',
                )}
          </Text>
        ) : (
          <Tree
            checkable={mode === 'assign' || codesType === 'available'}
            className={
              codesType === 'available' ? 'rc-tree-available' : undefined
            }
            disabled={isDisabledWithoutProposalSelected}
            expandedKeys={expandedKeys}
            onExpand={setExpandedKeys}
            showLine={true}
            treeData={displayedCodes}
            expandAction={'doubleClick'}
            onCheck={handleCheck}
            defaultCheckedKeys={
              isEditingAssignedCodesOfProposal
                ? getAllTreeKeys(displayedCodes)
                : undefined
            }
            switcherIcon={({ isLeaf }) =>
              isLeaf ? null : <Icon as={FiChevronRight} boxSize={3} />
            }
            {...propsTree}
          />
        )}
      </Box>

      {mode === 'manage' && codesType === 'available' && (
        <Button
          variant='danger'
          ml={'auto'}
          onClick={handleDeleteCodes}
          isDisabled={selectedCodeIds.length === 0}
          isLoading={deleteCodes.isLoading}
        >
          {t('monitoring.deleteSelectedCodes', 'Usuń wybrane')}
        </Button>
      )}
    </>
  );

  if (mode === 'assign') {
    return (
      <Grid gap={2}>
        <ExpandCollapseControls
          mode={mode}
          onCollapse={collapseAll}
          onExpand={expandAll}
          isDisabled={displayedCodes.length === 0}
        />
        {treeContent}
      </Grid>
    );
  }

  return (
    <Box
      as={Card}
      p={5}
      minW={0}
      variant={'blueLight'}
      borderWidth={1}
      borderColor={codesType === 'assigned' ? 'darkGreen' : 'WPPOrange.300'}
    >
      <Grid gap={4}>
        <HStack gap={4} justifyContent='space-between' alignItems='flex-start'>
          <Box>
            <Heading
              as='h2'
              fontSize='lg'
              color={codesType === 'assigned' ? 'darkGreen' : 'WPPOrange.300'}
            >
              {title}
            </Heading>
            {codesType === 'assigned' && (
              <Text fontSize='sm' color='palette.gray.main' mt={4}>
                {t(
                  'monitoring.codesAssignedSubheaderText',
                  'Kody przypisane do wybranych produktów, aby je edytować przejdź do widoku koszyków kampanii > Panel dopasowywania',
                )}
              </Text>
            )}
          </Box>
          <ExpandCollapseControls
            mode={mode}
            onCollapse={collapseAll}
            onExpand={expandAll}
            isDisabled={displayedCodes.length === 0}
          />
        </HStack>
        {treeContent}
        {codesType === 'assigned' && (
          <Button
            as={Link}
            to={campaignCodesAssignPage(campaignId)}
            justifySelf='end'
            variant='secondary'
          >
            {t(
              'campaign.goToCodesAssignPage',
              'Przejdź do widoku dopasowywania',
            )}
          </Button>
        )}
      </Grid>
    </Box>
  );
};
